/* Scroll reveal for the sample microbiome report card (lf-testkit-report.liquid).
 * Ports the ldv2 test-kit behaviour: the diversity dial sweeps its arc and the
 * three metric bars grow from zero the first time the card enters the viewport.
 * Loaded with defer only when the section's "Animate on scroll" setting is on.
 *
 * The pre-animation state is applied HERE, not in static CSS. Liquid renders the
 * card at its final values, so a browser with JS off, or a script that never
 * loads, shows the real graphic rather than an empty one. ldv2 puts the from-state
 * in the stylesheet and blanks the card in exactly those cases; this does not.
 * The trade is a possible flash of the finished graphic before arming, which no
 * one can see while the card sits below the fold where the template places it.
 *
 * Winding back is also why this is a custom element rather than an init that runs
 * once at page load: connectedCallback re-fires when the theme editor re-injects the
 * section, so the element can never be left holding a wind-back nobody will release.
 */
(() => {
  'use strict';

  if (customElements.get('lf-reveal')) return;

  /* Matches ldv2: fires a little before the card is fully in view, so the motion
   * reads as a response to the scroll rather than something already finished. */
  const ROOT_MARGIN = '0px 0px -10% 0px';

  class LfReveal extends HTMLElement {
    #io = null;
    #motionQuery = null;
    #onMotionChange = null;

    connectedCallback() {
      /* Nothing to animate means nothing to arm: leave the final values alone. */
      if (!this.querySelector('[data-lf-reveal]')) return;

      /* The theme editor never arms. Arming is a WIND-BACK of what Liquid already
       * rendered, released by an IntersectionObserver; the editor re-injects section
       * DOM on every setting change and can do it while the section is scrolled out
       * of the preview or briefly detached, so the observer may never fire and the
       * card sits stuck at an empty dial and zero-width bars. That reads as broken
       * content to a merchant who is only editing the numbers. Skipping the wind-back
       * shows the finished card, which is the state they are editing towards. */
      if (window.Shopify && window.Shopify.designMode) return;

      this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#onMotionChange = () => {
        if (this.#motionQuery.matches) this.#reveal();
      };
      if (typeof this.#motionQuery.addEventListener === 'function') {
        this.#motionQuery.addEventListener('change', this.#onMotionChange);
      }

      /* Reduced motion, or a browser without IntersectionObserver, keeps the
       * card exactly as Liquid rendered it. Never arm what cannot be released. */
      if (this.#motionQuery.matches || typeof IntersectionObserver === 'undefined') return;

      this.classList.add('is-armed');

      this.#io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#reveal();
        }
      }, { rootMargin: ROOT_MARGIN });
      this.#io.observe(this);
    }

    disconnectedCallback() {
      this.#io?.disconnect();
      this.#io = null;
      if (this.#motionQuery && typeof this.#motionQuery.removeEventListener === 'function') {
        this.#motionQuery.removeEventListener('change', this.#onMotionChange);
      }
    }

    /* One-way and idempotent. Dropping `is-armed` returns every value to the one
     * Liquid rendered, which the CSS transitions animate towards. */
    #reveal() {
      this.#io?.disconnect();
      this.#io = null;
      this.classList.remove('is-armed');
    }
  }

  customElements.define('lf-reveal', LfReveal);
})();
